/*
  Science video section.

  Three jobs, all optional: swap the background video when a card is selected, load the
  first source lazily, and drive the mobile Back / Next buttons over a native scroll-snap
  track. Without this file the section still works: the panels scroll and snap, the poster
  or placeholder shows, no video is ever requested.

  No autoplay under prefers-reduced-motion, and no source attached in that case, so
  nothing is downloaded.
*/
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReduced() {
    return !!(REDUCED && REDUCED.matches);
  }

  function ScienceVideo(root) {
    this.root = root;
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-asv-card]'));
    if (!this.cards.length) return;

    this.track = root.querySelector('[data-asv-track]');
    this.video = root.querySelector('[data-asv-video]');
    this.posters = Array.prototype.slice.call(root.querySelectorAll('[data-asv-poster]'));
    this.fallback = root.querySelector('[data-asv-fallback]');
    this.nav = root.querySelector('[data-asv-nav]');
    this.prev = root.querySelector('[data-asv-prev]');
    this.next = root.querySelector('[data-asv-next]');
    this.countLabel = root.querySelector('[data-asv-count-label]');
    this.toggles = Array.prototype.slice.call(root.querySelectorAll('[data-asv-toggle]'));
    this.index = 0;
    this.observer = null;

    this.bindToggles();
    this.bindNav();
    this.bindScroll();
    this.observe();
    this.sync();
  }

  ScienceVideo.prototype.bindToggles = function () {
    var self = this;
    this.toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.select(parseInt(btn.getAttribute('data-asv-toggle'), 10) || 0, true);
      });
      btn.addEventListener('keydown', function (event) {
        var last = self.cards.length - 1;
        var target = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = self.index + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = self.index - 1;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = last;
        if (target === null) return;
        event.preventDefault();
        target = Math.max(0, Math.min(last, target));
        self.select(target, true);
        var nextBtn = self.toggles[target];
        if (nextBtn) nextBtn.focus();
      });
    });
  };

  ScienceVideo.prototype.bindNav = function () {
    var self = this;
    // The nav ships hidden so it never appears without a working script behind it.
    if (this.nav) this.nav.hidden = false;
    if (this.prev) {
      this.prev.addEventListener('click', function () {
        self.select(self.index - 1, true);
      });
    }
    if (this.next) {
      this.next.addEventListener('click', function () {
        self.select(self.index + 1, true);
      });
    }
  };

  // Mirror a finger swipe on the native track back into the selected index.
  ScienceVideo.prototype.bindScroll = function () {
    if (!this.track) return;
    var self = this;
    var timer = null;
    this.track.addEventListener(
      'scroll',
      function () {
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          var middle = self.track.scrollLeft + self.track.clientWidth / 2;
          var closest = 0;
          var best = Infinity;
          self.cards.forEach(function (card, i) {
            var center = card.offsetLeft + card.offsetWidth / 2;
            var distance = Math.abs(center - middle);
            if (distance < best) {
              best = distance;
              closest = i;
            }
          });
          if (closest !== self.index) self.select(closest, false);
        }, 90);
      },
      { passive: true }
    );
  };

  ScienceVideo.prototype.select = function (index, scrollIntoView) {
    var last = this.cards.length - 1;
    if (index < 0 || index > last || index === this.index) return;
    this.index = index;
    this.sync();
    if (scrollIntoView && this.track) {
      var card = this.cards[index];
      this.track.scrollTo({
        left: card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2,
        behavior: prefersReduced() ? 'auto' : 'smooth'
      });
    }
    this.load();
  };

  ScienceVideo.prototype.sync = function () {
    var i = this.index;
    this.cards.forEach(function (card, n) {
      card.classList.toggle('is-active', n === i);
    });
    this.toggles.forEach(function (btn, n) {
      btn.setAttribute('aria-pressed', n === i ? 'true' : 'false');
    });
    this.posters.forEach(function (img) {
      img.hidden = parseInt(img.getAttribute('data-asv-poster'), 10) !== i;
    });
    // The section fallback only shows while the active card has no still of its own.
    if (this.fallback) {
      this.fallback.hidden = this.posters.some(function (img) {
        return parseInt(img.getAttribute('data-asv-poster'), 10) === i;
      });
    }
    if (this.countLabel) {
      this.countLabel.textContent = i + 1 + ' / ' + this.cards.length;
    }
    if (this.prev) this.prev.disabled = i === 0;
    if (this.next) this.next.disabled = i === this.cards.length - 1;
  };

  // Only attach a source once the section is near the viewport, and never under
  // reduced motion.
  ScienceVideo.prototype.observe = function () {
    if (!this.video || prefersReduced()) return;
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }
    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.load();
        });
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this.root);
  };

  ScienceVideo.prototype.load = function () {
    if (!this.video || prefersReduced()) return;
    if (!this.observer && !this.seen) this.seen = true;
    var card = this.cards[this.index];
    var raw = card && card.getAttribute('data-asv-src');
    if (!raw) {
      // This card is a still only. Stop the player and let the poster show through.
      this.video.classList.remove('is-ready');
      this.video.removeAttribute('src');
      this.video.load();
      return;
    }
    var urls = raw.split('|').filter(Boolean);
    if (!urls.length) return;
    var url = urls[urls.length - 1];
    if (this.video.getAttribute('src') === url) return;
    var self = this;
    this.video.classList.remove('is-ready');
    this.video.setAttribute('src', url);
    this.video.load();
    var ready = function () {
      self.video.classList.add('is-ready');
    };
    this.video.addEventListener('playing', ready, { once: true });
    var play = this.video.play();
    if (play && typeof play.catch === 'function') {
      play.catch(function () {
        // Autoplay refused. The poster stays, which is the correct fallback.
        self.video.classList.remove('is-ready');
      });
    }
  };

  function init() {
    Array.prototype.slice.call(document.querySelectorAll('[data-asv]')).forEach(function (root) {
      if (root.dataset.asvReady === '1') return;
      root.dataset.asvReady = '1';
      new ScienceVideo(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor re-renders a section in place, so re-initialise on its events.
  document.addEventListener('shopify:section:load', init);
})();
